import React, {useState, useEffect} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {
  FaPhone,
  FaComment,
  FaPhoneSlash,
  FaCommentSlash,
  FaEyeSlash,
} from "react-icons/fa";
import presentationService from "../../../services/presentationService";
import {
  isUUID,
  validateDialTarget,
} from "../../../utils/miscUtils";
import {getTopicVersionWithPrompts} from "../../../services/topicService";
import personaService from "../../../services/personaService";
import {useNavigate} from "react-router-dom";
import PresentationDisplayInfoModal from "./PresentationDisplayInfoModal";
import yaml from "js-yaml";
import {useToast} from "../../common/ToastContext";
import DebuggerStateManager from "../../prompts/DebugStateManager";

const PresentationDisplay = () => {
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const [presentation, setPresentation] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [sessionType, setSessionType] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const {showToast} = useToast();

  useEffect(() => {
    const fetchPresentation = async () => {
      const fetched = isUUID(id)
        ? await presentationService.getById(id)
        : await presentationService.getByUrlName(id);
      if (!fetched) {
        setError(`Presentation '${id}' not found`);
        return;
      }
      setPresentation(fetched);
      if (fetched.configuration.length > 0) {
        setSelectedConfig(fetched.configuration[0]);
      }
    };
    fetchPresentation();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      const topic = await getTopicVersionWithPrompts(
        selectedConfig.topic.key,
        selectedConfig.topic.version,
      );
      if (!topic.deleted_at) {
        setSelectedTopic(topic);
      } else {
        setSelectedTopic(null);
      }

      const persona = await personaService.getLatest(selectedConfig.persona.id);
      setSelectedPersona(persona);
    };
    if (selectedConfig && selectedConfig.topic && selectedConfig.persona) {
      fetchData();
    }
  }, [selectedConfig]);

  const handleConfigSelect = (cfg) => {
    setSelectedConfig(cfg);
    setSessionStarted(false);
    setSessionType(null);
  };

  const handleLaunchSession = (type) => {
    if (sessionStarted) {
      // stop the session, ensure the debugging is ended
      setSessionStarted(false);
      window.debuggerState.endDebugging();
      if (type === "call") {
        showToast(
          "The session has reset. Your phone call, if still in progress, will continue until you disconnect on your phone.",
          "info",
        );
      }
      return;
    }
    // open the modal to start a new debug session
    setModalType(type);
    setShowModal(true);
  };

  const buildLaunchConfig = (modality, phoneNbr, userName, selectedTopic) => {
    const variables = selectedConfig?.topic?.variables || "";
    const localVariables = yaml.load(variables) || {};
    Object.keys(localVariables).forEach((key) => {
      localVariables[key] = (localVariables[key] ?? "").toString();
      if (key === "patient_first_name" || key === "first_name") {
        localVariables[key] = userName;
      }
    });
    const config = {
      persona_id: selectedPersona.id,
      subject: null,
      modality: modality,
      variables: localVariables,
      recipient_phone_number: phoneNbr,
      topic: {
        key: selectedTopic.key,
        version: selectedTopic.version,
      },
    };
    if (!config.variables) {
      config.variables = {};
    }
    const yaml_ = yaml.dump(config);
    return yaml_;
  };

  const handleModalSubmit = (type, userName, userPhone) => {
    const phoneNbr = validateDialTarget(userPhone, type);

    if (phoneNbr.error) {
      showToast(phoneNbr.error, "error");
      return;
    }

    setShowModal(false);

    if (!window.debuggerState) {
      showToast("Debugger is not initialized", "error");
      return;
    }

    if (window.debuggerState.isDebugging) {
      window.debuggerState.endDebugging();
      setSessionStarted(false);
      setSessionType(null);
    } else {
      const detail = {
        name: "external-presentation",
        content: buildLaunchConfig(
          type === "call" ? "voice" : "text",
          phoneNbr.formatted,
          userName,
          selectedTopic,
        ),
      };
      window.dispatchEvent(
        new CustomEvent("launchConfiguration", {
          detail: detail,
        }),
      );
      setSessionStarted(true);
      setSessionType(type);
    }
  };

  const handleNavigateHome = () => {
    navigate("/presentations");
  };

  const handleReturnToEditing = () => {
    navigate(`/presentations/?id=${id}`);
  };

  if (!presentation || !selectedTopic || !selectedPersona) {
    let message;
    if (!presentation) {
      message = "No presentation found. Please check the URL and try again.";
    } else if (!selectedTopic) {
      message =
        "No topic found for the selected presentation. Please check the presentation and try again.";
    } else if (!selectedPersona) {
      message =
        "No persona found for the selected presentation. Please check the presentation and try again.";
    }

    if (!error) {
      return (
        <div className="fixed left-1/2 transform -translate-x-1/2 z-[9999] opacity-100">
          <div className="flex items-center p-4 rounded-lg shadow-lg border bg-gray-800 border-red-400">
            <div className="text-sm font-medium text-gray-200">{message}</div>
          </div>
          {presentation && (
            <button
              onClick={() => handleNavigateHome(null)}
              className="flex items-center mx-auto text-gray-800 hover:text-gray-400"
            >
              Return to Presentations Page
            </button>
          )}
        </div>
      );
    } else {
      return (
        <div
          className="fixed top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2 bg-yellow-500/30 p-2 rounded-xl text-red-600">
          {error}
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col h-screen bg-white">
      <DebuggerStateManager/>
      <header className="p-2 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1">
            <img
              src="/lumeris_logo.svg"
              alt="Lumeris"
              className="h-12 cursor-pointer"
              onClick={handleNavigateHome}
            />
          </div>
          <div className="flex-1 text-center">
            {searchParams.get("preview") && (
              <button
                title="Close Preview"
                onClick={handleReturnToEditing}
                className="flex items-center bg-green-500 text-white px-4 py-2 rounded-full shadow-lg transition transform hover:scale-105 cursor-pointer hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
              >
                <FaEyeSlash className="mr-2"/>
                <span>Close Preview</span>
              </button>
            )}
          </div>
          <div className="flex-1">
            {(presentation.whitelabel_org_name ||
              presentation.whitelabel_logo_url) && (
              <div className="flex justify-end items-center">
                {presentation.whitelabel_org_name && (
                  <div className="text-2xl font-sans text-gray-700 mr-4">
                    {presentation.whitelabel_org_name}
                  </div>
                )}
                {presentation.whitelabel_logo_url && (
                  <img
                    src={presentation.whitelabel_logo_url}
                    alt={presentation.whitelabel_org_name || "Partner Logo"}
                    className="h-12 max-w-[200px] object-contain"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-grow">
        <div className="flex-1 flex flex-col p-6 ml-4 mb-4 items-center">
          <div className="flex space-x-2 mb-6">
            {presentation.configuration.map((cfg) => {
              const isOnlyConfig = presentation.configuration.length === 1;
              return (
                <div
                  key={JSON.stringify(cfg)}
                  className={`flex items-center justify-center text-xl px-6 py-3 rounded-xl min-w-[200px] ${
                    selectedConfig?.topic.key === cfg.topic.key
                      ? "bg-blue-600 text-white"
                      : isOnlyConfig
                        ? "bg-gray-300 text-gray-600 cursor-default"
                        : "bg-gray-300 text-gray-600 hover:bg-gray-200 cursor-pointer"
                  }`}
                  {...(!isOnlyConfig
                    ? {onClick: () => handleConfigSelect(cfg)}
                    : {})}
                >
                  {cfg.topic.name}
                </div>
              );
            })}
          </div>

          {selectedConfig && (
            <div className="flex-grow mb-6 p-16 rounded w-full">
              <p
                className="whitespace-pre-line"
                style={{
                  fontFamily: ["Georgia", "Times New Roman", "Times", "serif"],
                  fontSize: "3.3vh",
                }}
              >
                {selectedConfig.topic.description}
              </p>
            </div>
          )}

          <div
            className="flex justify-center gap-4 pb-6"
            style={{marginTop: "0px"}}
          >
            <button
              onClick={() => handleLaunchSession("call")}
              className={`flex items-center justify-center w-40 ${
                sessionStarted
                  ? sessionType === "call"
                    ? "bg-red-500 hover:bg-red-700"
                    : "hidden"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white text-xl px-6 py-3 rounded-xl min-w-[200px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200`}
            >
              {sessionStarted && sessionType === "call" ? (
                <>
                  <FaPhoneSlash className="mr-2"/> Reset
                </>
              ) : (
                <>
                  <FaPhone className="mr-2"/> Start Call
                </>
              )}
            </button>
            {sessionStarted || <div className="w-10"></div>}
            <button
              onClick={() => handleLaunchSession("text")}
              className={`flex items-center justify-center w-40 ${
                sessionStarted
                  ? sessionType === "text"
                    ? "bg-red-500 hover:bg-red-700"
                    : "hidden"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white text-xl px-6 py-3 rounded-xl min-w-[200px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200`}
            >
              {sessionStarted && sessionType === "text" ? (
                <>
                  <FaCommentSlash className="mr-2"/> Reset
                </>
              ) : (
                <>
                  <FaComment className="mr-2"/> Start Text
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <PresentationDisplayInfoModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        type={modalType}
      />
    </div>
  );
};

export default PresentationDisplay;
